export default class HasDropdownDirective {

	constructor(
		$timeout
	) {
		this.$timeout = $timeout;
		this.restrict = 'A';
		this.scope = false;
	} link(scope, element, attributes, controller) {
		const node = element[0];
		let opened_ = null;
		let previouslyFocusedElement = null; // Store the element that had focus before opening dropdown
		let isHandlingKeyEvent = false; // Flag to prevent double handling
		// const consumer = attributes.hasDropdownConsumer !== undefined ? scope.$eval(attributes.hasDropdownConsumer) : null;
		const trigger = attributes.hasDropdown !== '' ? node.querySelector(attributes.hasDropdown) : node;

		// Ensure trigger element exists
		if (!trigger) {
			console.warn('HasDropdownDirective: Trigger element not found for selector:', attributes.hasDropdown);
			return;
		}		// Get focusable elements within the dropdown
		const getFocusableElements = () => {
			const dropdown = node.querySelector('[dropdown]');
			if (!dropdown) return [];			// For custom-select, focus on .option elements specifically
			const customSelectOptions = dropdown.querySelectorAll('.option');
			if (customSelectOptions.length > 0) {
				return Array.from(customSelectOptions).filter(el => {
					// Check if element is visible
					if (el.offsetParent === null) return false;

					// Check if parent li has disabled class or if any parent has disabled/ng-disabled
					const parentLi = el.closest('li');
					if (parentLi && parentLi.classList.contains('disabled')) return false;

					// Check for ng-disabled or other disabled attributes
					if (el.closest('.disabled, [ng-disabled="true"], [disabled]')) return false;

					return true;
				});
			}

			// Fallback to standard focusable elements for other dropdown types
			const selectors = [
				'a[href]:not([disabled]):not([tabindex="-1"])',
				'button:not([disabled]):not([tabindex="-1"])',
				'textarea:not([disabled]):not([tabindex="-1"])',
				'input:not([disabled]):not([tabindex="-1"]):not([style*="opacity: 0"])',
				'select:not([disabled]):not([tabindex="-1"])',
				'label[tabindex]:not([tabindex="-1"]):not([disabled])',
				'[tabindex]:not([tabindex="-1"]):not([disabled])'
			];

			return Array.from(dropdown.querySelectorAll(selectors.join(', ')))
				.filter(el => {
					// Only visible elements and not disabled
					if (el.offsetParent === null || el.closest('.disabled')) return false;

					// Exclude hidden radio inputs
					if (el.type === 'radio' && (
						el.style.opacity === '0' ||
						el.style.visibility === 'hidden' ||
						el.getAttribute('tabindex') === '-1'
					)) return false;

					return true;
				});
		};		// Manage tabindex of dropdown elements for keyboard navigation
		const setDropdownTabIndex = (tabIndex) => {
			const dropdown = node.querySelector('[dropdown]');
			if (!dropdown) return;

			// First check if we have custom-select options
			const customSelectOptions = dropdown.querySelectorAll('.option');
			if (customSelectOptions.length > 0) {
				// For custom-select, manage tabindex on .option elements
				customSelectOptions.forEach(element => {
					if (tabIndex === -1) {
						element.setAttribute('tabindex', '-1');
					} else {
						// Check if parent li is disabled
						const parentLi = element.closest('li');
						if (parentLi && parentLi.classList.contains('disabled')) {
							element.setAttribute('tabindex', '-1');
						} else {
							element.setAttribute('tabindex', '0');
						}
					}
				});
				return;
			}

			// Fallback for other dropdown types
			const focusableElements = dropdown.querySelectorAll('a, button, input, select, textarea, label[tabindex], [tabindex], li[ng-click], span[ng-click]');
			focusableElements.forEach(element => {
				// Skip hidden radio inputs
				if (element.type === 'radio' && (
					element.style.opacity === '0' ||
					element.style.visibility === 'hidden' ||
					element.getAttribute('tabindex') === '-1'
				)) {
					element.setAttribute('tabindex', '-1');
					return;
				}

				// Skip elements that are disabled via Angular
				const angularDisabled = element.hasAttribute('ng-disabled') &&
					element.getAttribute('ng-disabled').includes('item.disabled');

				if (tabIndex === -1) {
					// Store original tabindex if it exists
					const originalTabIndex = element.getAttribute('tabindex');
					if (originalTabIndex !== null) {
						element.setAttribute('data-original-tabindex', originalTabIndex);
					}
					element.setAttribute('tabindex', '-1');
				} else {
					// Restore original tabindex or set to 0 for focusable elements
					const originalTabIndex = element.getAttribute('data-original-tabindex');
					if (originalTabIndex !== null) {
						element.setAttribute('tabindex', originalTabIndex);
						element.removeAttribute('data-original-tabindex');
					} else if (element.tagName === 'LABEL' || element.hasAttribute('ng-click')) {
						// For label elements and clickable elements, set tabindex to 0
						element.setAttribute('tabindex', '0');
					} else {
						element.removeAttribute('tabindex');
					}
				}
			});
		};

		const onClick = (event) => {
			// Prevent default behavior for anchor links with javascript: href
			if (event.target.tagName === 'A' && event.target.href && event.target.href.startsWith('javascript:')) {
				event.preventDefault();
			}

			if (opened_ === null) {
				previouslyFocusedElement = document.activeElement;
				openDropdown();
			} else if (event.target !== trigger && !trigger.contains(event.target)) {
				closeDropdown(true);
			}
		}; const onKeyDown = (event) => {
			// Prevent double handling
			if (isHandlingKeyEvent) return;
			isHandlingKeyEvent = true;

			try {
				if (event.key === 'Enter' || event.key === ' ') {
					// Open dropdown with Enter or Space
					if (opened_ === null) {
						event.preventDefault();
						previouslyFocusedElement = event.target;
						openDropdown();
					} else {
						// If dropdown is open and we're focused on an option, activate it and close
						const focusedElement = document.activeElement;
						if (focusedElement && focusedElement.classList.contains('option')) {
							event.preventDefault();
							focusedElement.click();
							// Small delay to ensure the click event is processed first
							setTimeout(() => {
								closeDropdown(true);
							}, 10);
						}
					}
				} else if (event.key === 'Escape') {
					// Close dropdown with Escape
					if (opened_ !== null) {
						event.preventDefault();
						closeDropdown(true);
					}
				} else if (opened_ !== null) {
					// Handle arrow keys within open dropdown
					const focusableElements = getFocusableElements();
					if (focusableElements.length === 0) return;

					const currentIndex = focusableElements.indexOf(document.activeElement);

					if (event.key === 'ArrowDown') {
						event.preventDefault();
						event.stopPropagation(); // Prevent event bubbling

						let nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;

						// Ensure we don't get stuck in an infinite loop
						let attempts = 0;
						while (attempts < focusableElements.length && focusableElements[nextIndex] && focusableElements[nextIndex].closest('.disabled, [ng-disabled="true"]')) {
							nextIndex = nextIndex < focusableElements.length - 1 ? nextIndex + 1 : 0;
							attempts++;
						}

						if (focusableElements[nextIndex]) {
							focusableElements[nextIndex].focus();
						}
					} else if (event.key === 'ArrowUp') {
						event.preventDefault();
						event.stopPropagation(); // Prevent event bubbling

						let prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;

						// Ensure we don't get stuck in an infinite loop
						let attempts = 0;
						while (attempts < focusableElements.length && focusableElements[prevIndex] && focusableElements[prevIndex].closest('.disabled, [ng-disabled="true"]')) {
							prevIndex = prevIndex > 0 ? prevIndex - 1 : focusableElements.length - 1;
							attempts++;
						}

						if (focusableElements[prevIndex]) {
							focusableElements[prevIndex].focus();
						}
					} else if (event.key === 'Tab') {
						// Allow tab to move between dropdown items
						if (!event.shiftKey) {
							// Tab forward
							const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : -1;
							if (nextIndex === -1) {
								// Let tab exit the dropdown naturally
								closeDropdown(false);
							} else {
								event.preventDefault();
								focusableElements[nextIndex].focus();
							}
						} else {
							// Shift+Tab backward
							const prevIndex = currentIndex > 0 ? currentIndex - 1 : -1;
							if (prevIndex === -1) {
								// Let shift+tab exit the dropdown naturally
								closeDropdown(false);
							} else {
								event.preventDefault();
								focusableElements[prevIndex].focus();
							}
						}
					}
				}
			} finally {
				// Reset flag after a short delay
				setTimeout(() => {
					isHandlingKeyEvent = false;
				}, 10);
			}
		}; const onDropdownKeyDown = (event) => {
			// Prevent double handling
			if (isHandlingKeyEvent) return;

			if (event.key === 'Escape') {
				event.preventDefault();
				closeDropdown(true);
			} else if (event.key === 'Enter' || event.key === ' ') {
				// Handle Enter and Space to activate the focused element
				const focusedElement = document.activeElement;
				if (focusedElement && node.contains(focusedElement)) {
					event.preventDefault();
					// Trigger click on the focused element
					focusedElement.click();

					// For custom-select options, close the dropdown after selection
					if (focusedElement.classList.contains('option')) {
						// Small delay to ensure the click event is processed first
						setTimeout(() => {
							closeDropdown(true);
						}, 10);
					}
				}
			}
			// Remove delegation to prevent double handling
		};
		const onDocumentClick = (event) => {
			const clickedInside = node === event.target || node.contains(event.target);
			if (!clickedInside) {
				closeDropdown();
			}
		}; const openDropdown = () => {
			if (opened_ === null) {
				opened_ = true;
				addDocumentListeners();
				scope.$root.$broadcast('onDroppedIn', node);
				const callback = attributes.droppedIn !== undefined ? scope.$eval(attributes.droppedIn) : () => { };
				callback(node, node.querySelector('[dropdown]'));

				// Set aria-expanded to true for accessibility
				trigger.setAttribute('aria-expanded', 'true');

				// Enable keyboard navigation for dropdown elements
				setDropdownTabIndex(0);

				// Focus management: move focus to first focusable element in dropdown
				this.$timeout(() => {
					const focusableElements = getFocusableElements();
					if (focusableElements.length > 0) {
						focusableElements[0].focus();
					}
				}, 50); // Small delay to ensure dropdown is rendered
			}
		}; const closeDropdown = (restoreFocus = false) => {
			if (opened_ !== null) {
				opened_ = null;
				removeDocumentListeners();
				scope.$root.$broadcast('onDroppedOut', node);
				const callback = attributes.droppedOut !== undefined ? scope.$eval(attributes.droppedOut) : () => { };
				callback(node, node.querySelector('[dropdown]'));

				// Set aria-expanded to false for accessibility
				trigger.setAttribute('aria-expanded', 'false');

				// Disable keyboard navigation for dropdown elements
				setDropdownTabIndex(-1);

				// Focus management: restore focus to trigger element
				if (restoreFocus && previouslyFocusedElement) {
					this.$timeout(() => {
						previouslyFocusedElement.focus();
						previouslyFocusedElement = null;
					}, 10);
				}
			}
		};
		scope.$on('onDroppedOut', ($scope, $node) => {
			if ($node === node) {
				node.classList.remove('opened');
			}
		});
		scope.$on('onDroppedIn', ($scope, $node) => {
			const opened = $node === node || node.contains($node);
			if (opened) {
				node.classList.add('opened');
				if (node.parentNode) {
					node.parentNode.classList.add('has-dropdown--opened');
				}
			} else {
				node.classList.remove('opened');
				if (node.parentNode) {
					node.parentNode.classList.remove('has-dropdown--opened');
				}
			}
		});
		scope.$on('onCloseDropdown', closeDropdown);
		scope.$on('onNavigateOut', closeDropdown);
		scope.$on('onNavigationTransitionIn', closeDropdown); const addListeners = () => {
			trigger.addEventListener('click', onClick);
			trigger.addEventListener('keydown', onKeyDown);
			// Make trigger focusable if it's not already
			if (!trigger.hasAttribute('tabindex') && trigger.tagName !== 'A' && trigger.tagName !== 'BUTTON') {
				trigger.setAttribute('tabindex', '0');
			}
			// Initialize aria-expanded attribute for accessibility
			if (!trigger.hasAttribute('aria-expanded')) {
				trigger.setAttribute('aria-expanded', 'false');
			}
		};

		const addDocumentListeners = () => {
			document.addEventListener('click', onDocumentClick);
			const dropdown = node.querySelector('[dropdown]');
			if (dropdown) {
				dropdown.addEventListener('keydown', onDropdownKeyDown);
			}
		};

		const removeListeners = () => {
			trigger.removeEventListener('click', onClick);
			trigger.removeEventListener('keydown', onKeyDown);
		};

		const removeDocumentListeners = () => {
			document.removeEventListener('click', onDocumentClick);
			const dropdown = node.querySelector('[dropdown]');
			if (dropdown) {
				dropdown.removeEventListener('keydown', onDropdownKeyDown);
			}
		}; addListeners();

		// Initialize: disable keyboard navigation for dropdown elements when closed
		setDropdownTabIndex(-1);

		scope.$on('$destroy', () => {
			removeListeners();
			removeDocumentListeners();
		});
	}

	static factory($timeout) {
		return new HasDropdownDirective($timeout);
	}

}

HasDropdownDirective.dropDownUid = 0;

HasDropdownDirective.factory.$inject = ['$timeout'];
